import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'
import { mapLanguageToPageProps } from 'interview/i18n'
import FetchWithSpinner from 'components/fetch-with-spinner'
import Form from 'components/form-components/form/form'
import { fetchEmbeddedJobOpeningAndCandidate } from 'interview/actions/job-opening'
import { PositionType, ResponseType } from 'types'
import JobOpeningService from 'interview/services/job-opening'
import { Flex } from 'components/styled/containers/flex'
import * as analytics from 'analytics/performance'
import {
  completeInterview,
  removeDraft,
  saveDraft,
} from 'interview/actions/candidate'
import AgreeTos from 'components/agree-tos'
import FormErrors from 'components/form-components/form-errors'
import { I18nPageType } from 'interview/i18n-types'
import QuestionResponseDialogue from 'interview/components/question-response-dialogue'
import { USE_FILE_UPLOAD } from 'features'
import styled, { css, keyframes } from 'styled-components'
import getCustomSupport from 'interview/selectors/custom-support'
import { Body, ExternalLink, Subtitle } from 'components/styled/typography'
import { Button } from 'components/styled/button'
import { CheckboxWrapper } from 'components/styled/checkbox/base-checkbox'
import { addError, addMessage } from 'actions'
import {
  getCandidateInfoForAmplitude,
  getCompanyAndInterviewInfoForAmplitude,
} from 'selectors'
import CandidateInfoForm from './form'
import Question from 'components/question'
import { CardGroup } from 'components/styled/card-group'

type CandidateType = {
  _id: string
  id?: string
  uuid?: string
  email?: string
  atsCandidateId?: string
  link?: string
  completed?: boolean
  agreeTOS: boolean
  videoResponses: ResponseType[]
}

interface EmbeddedProfileInterviewProps {
  params: { token: string }
  location?: {
    query: { email?: string; candidate?: string }
  }
  interview: PositionType
  candidate: CandidateType
  dispatch: any
  i18n: I18nPageType
  globalRecorder?: string
  customSupport?: {
    companyName?: string
    type?: string
    value?: string
  }
  language?: string
  candidateForm?: { $form: { valid: boolean } }
}

const mapStateToProps = (state: any) => ({
  interview: state.interview.jobOpening,
  candidate: state.candidate,
  candidateForm: state.candidateForm,
  language: state.language,
  globalRecorder: state.global.globalRecorder,
  customSupport: getCustomSupport(state),
})

const $PulseStyle = css`
  animation: ${({ theme }) => theme.animation.pulse} 1.2s infinite;
`

const PulsingSubmitButton = styled(Button)<{ disabled: boolean }>`
  width: 50%;
  padding: ${({ theme }) => `0 ${theme.space.s12} 0.5rem ${theme.space.s12}}`};

  button {
    ${({ disabled }) => !disabled && $PulseStyle};
    width: 100% !important;
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    width: 100% !important;
  }
`

const StyledAgreeTos = styled(AgreeTos)`
  > ${CheckboxWrapper} {
    margin: 0 !important;
  }
`

const EmbeddedProfileInterviewContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 700px;
  margin: ${({ theme }) => theme.space.s8} auto;
  padding: 0 ${({ theme }) => theme.space.s8};
  // reverting whole page content margin-bottom
  margin-bottom: ${({ theme }) => `-${theme.space.s40}`};

  ${({ theme }) => theme.mediaQuery.smallTablet} {
    width: 100%;
  }
`

const StyledProfileContainer = styled(Flex)`
  width: 700px;
  flex-direction: column;

  ${({ theme }) => theme.mediaQuery.smallTablet} {
    width: 100%;
  }
`

const FooterContainer = styled(Flex)`
  width: 100%;
  margin-top: ${({ theme }) => theme.space.s4};
  justify-content: space-between;

  ${({ theme }) => theme.mediaQuery.phone} {
    margin-top: 0;
    flex-direction: column-reverse;
  }
`

const StyledForm = styled(Form)`
  ${({ theme }) => theme.mediaQuery.smallTablet} {
    width: 100%;
  }
`

function preventEnterSubmit(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault()
  }
}

const EmbeddedProfileInterview = ({
  params,
  location,
  interview,
  candidate,
  dispatch,
  i18n,
  globalRecorder = '',
  customSupport,
  language,
  candidateForm,
}: EmbeddedProfileInterviewProps) => {
  const [submitting, setSubmitting] = useState(false)
  const [finished, setFinished] = useState(false)
  const [eventProps, setEventProps] = useState({})
  const hasCandidateInfo = !!(
    location &&
    location.query &&
    location.query.candidate &&
    location.query.email
  )

  const getEventProps = (interview: PositionType, candidate: CandidateType) => {
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)
    return {
      ...companyAndInterviewInfo,
      ...candidateInfo,
    }
  }

  useEffect(() => {
    dispatch(actions.reset('candidate'))
    return () => {
      dispatch(actions.reset('candidate'))
    }
  }, [])

  useEffect(() => {
    if (candidate.completed) {
      setFinished(true)
    }
    setEventProps(getEventProps(interview, candidate))
  }, [interview, candidate])

  const onCreateCandidate = () => {
    setSubmitting(true)

    return JobOpeningService.createCandidate(
      interview.profileUuid,
      candidate,
      true
    )
      .then((candidate: CandidateType) => {
        setSubmitting(false)
        analytics.onClickEvent({
          locationId: 'embeddedProfileInterviewPage',
          id: 'submitFormAndCreateDraftCandidateButton',
          properties: eventProps,
        })
        dispatch(actions.change('candidate', candidate))
      })
      .catch(err => {
        setSubmitting(false)
        dispatch(addError(err))
      })
  }

  const onSubmit = () => {
    setSubmitting(true)
    dispatch(completeInterview(candidate, true))
      .then((savedCandidate: CandidateType) => {
        setSubmitting(false)
        analytics.onClickEvent({
          locationId: 'embeddedProfileInterviewPage',
          id: 'submitCandidateInterviewButton',
          properties: eventProps,
        })
        setFinished(true)
        if (hasCandidateInfo) {
          dispatch(actions.change('candidate', savedCandidate))
          dispatch(
            addMessage({
              style: 'success',
              text: i18n.embeddedProfileInterview.thankYou,
            })
          )
        } else {
          dispatch(actions.reset('candidate'))
        }
      })
      .catch((err: any) => {
        setSubmitting(false)
        dispatch(addError(err))
      })
  }

  const onFormChange = (model: string, value: any) => {
    dispatch(actions.change(model, value))
  }

  const onVideoChange = (questionId: string) => {
    const videoResponse = candidate.videoResponses.find(
      r => r.questionId === questionId
    )
    const useUpload =
      USE_FILE_UPLOAD ||
      globalRecorder === 'upload' ||
      (interview.allowUpload &&
        videoResponse?.recorder &&
        videoResponse.recorder === 'upload')

    dispatch(
      saveDraft({
        isMobile: useUpload,
        uuid: candidate.uuid,
        response: videoResponse,
      })
    )
    analytics.onChangeEvent({
      locationId: 'embeddedProfileInterviewPage',
      id: 'video',
      properties: eventProps,
    })
  }

  const supportCenterLink = () => {
    switch (language) {
      case 'fi':
        return 'https://support.recright.com/fi/'
      case 'se':
        return 'https://support.recright.com/sv/'
      default:
        return 'https://support.recright.com/en/'
    }
  }

  const onVideoRemove = (questionId: string) => {
    const { videoResponses } = candidate
    const response =
      videoResponses && videoResponses.find(r => r.questionId === questionId)

    const payload = {
      uuid: candidate.uuid,
      response,
    }

    dispatch(removeDraft(payload))

    analytics.onChangeEvent({
      locationId: 'embeddedProfileInterviewPage',
      id: 'videoDraftRemoveButton',
      properties: {
        isEmbedded: eventProps,
      },
    })
  }

  // TODO: ARE VIDEOREPONSES ALWAYS REQUIRED HERE? EVEN WHEN THERE ARE MULTIPLE? ASSUMING SO FOR NOW
  const areAllAnswered = () => {
    let allAnswered = true
    const { questions } = interview
    const { videoResponses } = candidate
    // Check that all videos are answered
    questions?.forEach(({ _id: questionId, answerable }) => {
      if (!answerable) {
        return
      }

      const response =
        videoResponses && videoResponses.find(r => r.questionId === questionId)
      if (!response || !response.has || response.beingProcessed) {
        allAnswered = false
      }
    })

    return allAnswered
  }

  const disableSubmit =
    (candidateForm && candidateForm.$form && !candidateForm.$form.valid) ||
    submitting ||
    !areAllAnswered()

  const savedCandidate = candidate && candidate._id && candidate.uuid
  const multipleQuestions =
    interview.questions && interview.questions.length > 1
  const showSubmit = !finished && candidate.uuid && candidate._id
  return (
    <FetchWithSpinner
      action={() =>
        fetchEmbeddedJobOpeningAndCandidate(params, location?.query)
      }
    >
      <EmbeddedProfileInterviewContainer id="embedded-profile-interview">
        {finished && !hasCandidateInfo ? (
          <Subtitle mt="s128" textAlign="center">
            {i18n.embeddedProfileInterview.thankYou}
          </Subtitle>
        ) : (
          <StyledForm
            model="candidate"
            // @ts-ignore what is the issue here? TODO fix type
            onSubmit={() => onSubmit()}
            onKeyPress={preventEnterSubmit}
          >
            <StyledProfileContainer>
              <>
                {candidate.agreeTOS ? (
                  <CandidateInfoForm
                    hasCandidateInfo={hasCandidateInfo}
                    onChange={onFormChange}
                  />
                ) : (
                  <StyledAgreeTos
                    model="candidate.agreeTOS"
                    agreed={candidate.agreeTOS}
                    onChange={(value: boolean) =>
                      onFormChange('candidate.agreeTOS', value)
                    }
                    privacyLink={
                      interview.company && interview.company.privacyPolicy
                    }
                    customSupport={customSupport}
                  >
                    <FormErrors model="candidate.agreeTOS" />
                  </StyledAgreeTos>
                )}
                {!savedCandidate && multipleQuestions && (
                  <CardGroup minWidth="s240">
                    {interview.questions &&
                      interview.questions.map(question => (
                        <Question
                          key={question._id}
                          question={question}
                          locationId="embeddedProfileInterviewPage"
                          videoEventProps={eventProps}
                        />
                      ))}
                  </CardGroup>
                )}
                {(!multipleQuestions ||
                  (multipleQuestions && savedCandidate)) && (
                  <QuestionResponseDialogue
                    questions={interview.questions}
                    responses={candidate.videoResponses}
                    useFileUpload={
                      !!(USE_FILE_UPLOAD || globalRecorder === 'upload')
                    }
                    isEmbedded
                    disableResponse={
                      candidateForm &&
                      candidateForm.$form &&
                      !candidateForm.$form.valid
                    }
                    allowUploadWithRecord={interview.allowUpload}
                    uuid={candidate.uuid}
                    onVideoRemove={(questionId: string) =>
                      onVideoRemove(questionId)
                    }
                    createDraftCandidate={
                      !multipleQuestions ? onCreateCandidate : undefined
                    }
                    onVideoChange={(questionId: string) => {
                      onVideoChange(questionId)
                    }}
                    locationId="embeddedProfileInterviewPage"
                    videoEventProps={eventProps}
                  />
                )}
              </>
              <FooterContainer
                justifyContent="space-between"
                mt="s12"
                alignItems="center"
              >
                <ExternalLink href={supportCenterLink()} target="_blank">
                  {i18n.embeddedProfileInterview.supportCenter}
                </ExternalLink>
                {!candidate.uuid &&
                  !candidate._id &&
                  interview.questions &&
                  interview.questions.length > 1 && (
                    <Button
                      onClick={onCreateCandidate}
                      iconRight="east"
                      disabled={
                        candidateForm &&
                        candidateForm.$form &&
                        !candidateForm.$form.valid
                      }
                      text={i18n.embeddedProfileInterview.continue}
                    />
                  )}
                {showSubmit && (
                  <PulsingSubmitButton
                    disabled={disableSubmit}
                    type="submit"
                    icon="send"
                    loading={submitting}
                    text={i18n.embeddedProfileInterview.submit}
                  />
                )}
              </FooterContainer>
            </StyledProfileContainer>
          </StyledForm>
        )}
      </EmbeddedProfileInterviewContainer>
    </FetchWithSpinner>
  )
}

export default connect(mapLanguageToPageProps(mapStateToProps))(
  EmbeddedProfileInterview
)
